import { useState } from 'react';
import { Eyebrow } from '../concept/eyebrow';
import { formatBodyFat, formatWeight } from '../lib/metric-format';

export interface ScanLogEntry {
  id: string;
  date: Date;
  weightKg?: number;
  bodyFatPercent?: number;
}

/**
 * The concept's scan log, newest first, with tap-two-to-compare.
 *
 * Selection is controlled: the page owns which two are picked, so the list
 * stays a presentation concern and the comparison can be opened from anywhere.
 *
 * Rows are captioned by week since the first scan, as the concept has them
 * ("Week 27 · latest"). Ordinals like "Scan 3 of 12" say nothing a coach can
 * act on; elapsed weeks is the axis the programme runs on.
 */
export const ScanLog: React.FC<{
  entries: ScanLogEntry[];
  selected?: string[];
  onToggle?: (id: string) => void;
  max?: number;
  /**
   * Whether each row carries its weight and body fat. Off where neither is
   * shown (retail), so a row is its date rather than a column of dashes.
   */
  readings?: boolean;
  /**
   * Something to do with one scan, beside its row — the phone's way into the
   * tape review, which otherwise lives on Settings, a desktop-only section.
   */
  rowAction?: (entry: ScanLogEntry) => React.ReactNode;
  className?: string;
}> = ({
  entries,
  selected = [],
  onToggle,
  max = 4,
  readings = true,
  rowAction,
  className = '',
}) => {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? entries : entries.slice(0, max);
  const first = entries[entries.length - 1];

  const caption = (e: ScanLogEntry, index: number) => {
    const weeks = first
      ? Math.round((e.date.getTime() - first.date.getTime()) / (7 * 24 * 3600 * 1000))
      : 0;
    if (index === 0 && entries.length > 1) return `Week ${weeks} · latest`;
    if (e.id === first?.id) return 'Week 0 · start';
    return `Week ${weeks}`;
  };

  return (
    <div
      data-testid="scan-log"
      className={`flex flex-col rounded-lg border border-border bg-card p-5 ${className}`}
    >
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <Eyebrow>Scans</Eyebrow>
        {/* What the removed "Compare scans" header button was pointing at,
            said where the gesture actually happens. It used to read "Compare
            two" in 10px next to the title, which named the feature without
            saying that tapping rows is how you get it. */}
        {onToggle && (
          <span className="text-right text-[10px] leading-snug text-primary">
            {selected.length === 0
              ? 'Tap two to compare'
              : selected.length === 1
                ? 'Tap one more to compare'
                : 'Comparing two'}
          </span>
        )}
      </div>

      {shown.map((e, i) => {
        const isSelected = selected.includes(e.id);
        const row = (
          <button
            key={rowAction ? undefined : e.id}
            type="button"
            onClick={() => onToggle?.(e.id)}
            disabled={!onToggle}
            className={`-mx-2 flex items-center justify-between rounded-md px-2 py-2.5 text-left transition-colors ${
              isSelected ? 'bg-accent ring-1 ring-primary/40' : onToggle ? 'hover:bg-shell' : ''
            } ${rowAction ? 'mr-0 min-w-0 flex-1' : i < shown.length - 1 ? 'border-b border-divider' : ''}`}
          >
            <span className="flex flex-col">
              <span
                className={`text-xs leading-snug ${i === 0 ? 'font-semibold' : 'font-normal'}`}
              >
                {e.date.toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
              <span className="text-[10px] leading-snug text-muted-foreground">
                {caption(e, i)}
              </span>
            </span>
            {readings && (
              <span className="tabular text-[11.5px] text-foreground-soft">
                {e.weightKg !== undefined ? formatWeight(e.weightKg) : '—'}
                {e.bodyFatPercent !== undefined ? ` · ${formatBodyFat(e.bodyFatPercent)}` : ''}
              </span>
            )}
          </button>
        );
        if (!rowAction) return row;
        return (
          <div
            key={e.id}
            data-testid="scan-log-row"
            className={`flex items-center gap-2 ${i < shown.length - 1 ? 'border-b border-divider' : ''}`}
          >
            {row}
            {rowAction(e)}
          </div>
        );
      })}

      <div className="flex-1" />

      {entries.length > max && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="scan-log-toggle"
          className="self-start pt-2.5 text-[11px] font-medium text-primary hover:underline"
        >
          {expanded ? 'Show fewer' : `All ${entries.length} scans`}
        </button>
      )}
    </div>
  );
};
